import { Injectable } from '@angular/core';
import { GcFlyoutService } from '@core/services/gc-flyout.service';
import { MyApplicationForUi } from '@core/typings/application.typing';
import { CommunicationsService } from '@features/communications/communications.service';
import { FileService } from '@yourcause/common/files';
import { FlyoutService } from '@yourcause/common/flyout';
import { I18nService } from '@yourcause/common/i18n';
import { ConfirmAndTakeActionService } from '@yourcause/common/modals';
import { AttachYCState, BaseYCService } from '@yourcause/common/state';
import { CommunicationDetailFlyoutComponent } from '../communication-detail-flyout/communication-detail-flyout.component';
import { ApplicantCommunicationResources } from '../resources/applicant-communications.resources';
import { ApplicantCommunicationState } from '../states/applicant-communications.state';
import { CommunicationForApplicant } from '../typings/applicant-communications.typing';

@AttachYCState(ApplicantCommunicationState)
@Injectable({ providedIn: 'root' })
export class ApplicantCommunicationService extends BaseYCService<ApplicantCommunicationState> {

  constructor (
    private flyoutService: FlyoutService,
    private gcFlyoutService: GcFlyoutService,
    private applicantCommunicationResources: ApplicantCommunicationResources,
    private i18n: I18nService,
    private confirmAndTakeActionService: ConfirmAndTakeActionService,
    private fileService: FileService,
    private communicationService: CommunicationsService
  ) {
    super();
  }

  get applicationCommunicationsMap () {
    return this.get('applicationCommunicationsMap');
  }

  get communicationFlyoutDetails () {
    return this.get('communicationFlyoutDetails');
  }

  get currentFlyoutApp () {
    return this.get('currentFlyoutApp');
  }

  /**
   * Set the Application Communications
   */
  async doSetApplicationCommunications (applicationId: number) {
    if (!this.applicationCommunicationsMap[applicationId]) {
      const result = await this.applicantCommunicationResources.getApplicationCommunications(applicationId);
      
      this.set('applicationCommunicationsMap', {
        ...this.applicationCommunicationsMap,
        [applicationId]: result.records
      });
    }
  }

  /**
   * Sets the application communications for a given application ID
   *
   * @param applicationId: Application ID
   * @returns if the set passed
   */
  async setApplicationCommunications (applicationId: number) {
    const {
      passed
    } = await this.confirmAndTakeActionService.genericTakeAction(
      () => this.doSetApplicationCommunications(applicationId),
      '',
      this.i18n.translate(
        'common:textErrorFetchingCommRecords',
        {},
        'There was an error fetching the communications'
      )
    );

    return passed;
  }

  /**
   * Get the Table Key to Use for Application Comms
   *
   * @param applicationId: Application ID
   * @returns the table key for the Application comms
   */
  getApplicationCommunicationTableKey (applicationId: number) {
    return `COMMUNICATIONS_${applicationId}`;
  }

  /**
   * Get the Flyout Detail
   * 
   * @param emailQueueId: Communication record ID
   * @returns the flyout detail
   */
  getFlyoutInfo (emailQueueId: number) {
    return this.communicationFlyoutDetails[emailQueueId];
  }

  /**
   * Opens the communication flyout for the given record
   *
   * @param comm: Communication record
   */
  async openCommunicationFlyout (
    comm: CommunicationForApplicant,
    application: MyApplicationForUi
  ): Promise<void> {
    this.set('currentFlyoutApp', application);

    this.gcFlyoutService.setInfoForFlyout<CommunicationForApplicant>(
      comm,
      this.getApplicationCommunicationTableKey(application.applicationId),
      'emailQueueId',
      this.applicationCommunicationsMap[application.applicationId]
    );
    const only1Record = this.gcFlyoutService.idsForFlyout.length === 1;
    await this.flyoutService.openFlyout(
      CommunicationDetailFlyoutComponent,
      {
        showIterator: !only1Record
      },
      this.gcFlyoutService.onNextFlyoutRecord,
      this.gcFlyoutService.onPreviousFlyoutRecord,
      this.prepareCommFlyout,
      this.gcFlyoutService.onInitialFlyoutRecord
    );
  }

  /**
   * Prepare the Communication Flyout
   */
  prepareCommFlyout = async (emailQueueId: number|string) => {
    if (!this.getFlyoutInfo(emailQueueId as number)) {
      const details = await this.applicantCommunicationResources.getApplicationCommunicationDetail(
        emailQueueId as number,
        this.currentFlyoutApp.applicationId
      );
      this.set('communicationFlyoutDetails', {
        ...this.communicationFlyoutDetails,
        [emailQueueId]: {
          ...details,
          body: this.communicationService.stripExtraText(details.body),
          emailAttachments: [
            ...details.emailAttachments,
            ...details.emailMergeDocuments
          ]
        }
      });
    }
  };

  /**
   * Download the File
   *
   * @param url: URL to download
   * @param fileName: File Name
   */
  async downloadFile (
    url: string,
    fileName: string
  ) {
    await this.confirmAndTakeActionService.genericTakeAction(
      () => this.fileService.downloadUrlAs(url, fileName),
      '',
      this.i18n.translate(
        'CONFIG:textErrorDownloadingTheFile',
        {},
        'There was an error downloading the file'
      )
    );
  }

}
